import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Modal } from 'reactstrap';

import axiosPrivate from 'config/axiosPrivate';
import { classes } from './modalClasses';
import './WordPressDeployModal.css';

const CMS_CREDENTIALS_MISSING_STATUS = 404;

const slugify = (text = '') =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#333',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontWeight: 600,
  fontSize: '14px',
  color: '#333',
  marginBottom: '6px',
};

const buttonPrimaryStyle = {
  padding: '10px 20px',
  border: 'none',
  borderRadius: 6,
  background: '#6458F1',
  color: '#FFFFFF',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const buttonSecondaryStyle = {
  padding: '10px 20px',
  border: '1px solid #E2E2E2',
  borderRadius: 6,
  background: '#FFFFFF',
  color: '#1A1A1A',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

export const WordPressDeployModal = ({
  isOpen,
  onClose,
  textId,
  websiteId,
  initialKeyword = '',
  initialContent = '',
  onDeploySuccess,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('form');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [content, setContent] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [helpOpen, setHelpOpen] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setStep('form');
    setUsername('');
    setPassword('');
    setKeyword(initialKeyword || '');
    setContent(initialContent || '');
    setSlug(slugify(initialKeyword || ''));
    setLoading(false);
    setError('');
    setHelpOpen(true);
  }, [isOpen, initialKeyword, initialContent]);
  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const deployToWordPress = async () => {
    const normalizedSlug = slugify(slug.trim());
    if (!normalizedSlug) {
      setError(t('Le slug est invalide.'));
      return false;
    }

    if (!keyword.trim()) {
      setError(t('Le mot-clé est obligatoire.'));
      return false;
    }

    if (!content.trim()) {
      setError(t('Le contenu est obligatoire.'));
      return false;
    }

    if (!textId) {
      setError(t('Identifiant du texte manquant.'));
      return false;
    }

    const response = await axiosPrivate.post(`/texts/external/deploy/${textId}`, {
      keyword: keyword.trim(),
      content: content.trim(),
      slug: normalizedSlug,
    });

    if (onDeploySuccess) {
      await onDeploySuccess(response?.data);
    }
    onClose();
    return true;
  };

  const handleDeploy = async (e) => {
    e.preventDefault();
    setError('');

    if (!slug.trim()) {
      setError(t('Le slug est obligatoire.'));
      return;
    }

    setLoading(true);
    try {
      await deployToWordPress();
    } catch (err) {
      if (err?.response?.status === CMS_CREDENTIALS_MISSING_STATUS) {
        setStep('login');
        setError('');
        return;
      }

      console.error('Erreur lors du déploiement WordPress:', err);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.code ||
        err.message ||
        t('Erreur lors du déploiement sur WordPress.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError(t('Veuillez renseigner le nom d’utilisateur et le mot de passe.'));
      return;
    }

    if (!websiteId) {
      setError(t('Identifiant du site web manquant.'));
      return;
    }

    setLoading(true);
    try {
      await axiosPrivate.put(`/project/website/cms/${websiteId}`, {
        username: username.trim(),
        password: password.trim(),
      });

      setStep('form');
      setError('');
      await deployToWordPress();
    } catch (err) {
      console.error('Erreur lors de la connexion ou du déploiement WordPress:', err);
      const isDeployError = err?.config?.url?.includes('/texts/external/deploy/');
      const isCmsError = err?.config?.url?.includes('/project/website/cms/');

      if (isCmsError && err?.response?.status === 404) {
        setError(t('Identifiants WordPress n\'existe pas. Veuillez réessayer.'));
        return;
      }

      if (isCmsError && err?.response?.status === 406) {
        setError(t('Les informations de connexion ne sont pas correctes.'));
        return;
      }

      setError(
        err?.response?.data?.message ||
        (isDeployError
          ? t('Erreur lors du déploiement sur WordPress.')
          : t('Identifiants WordPress invalides. Veuillez réessayer.'))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="wp-deploy-modal"
      style={step === 'form' ? classes.largeModalCustom : classes.modalCustom}
      isOpen={isOpen}
      toggle={handleClose}
    >
      <div style={classes.modalHeader} className="modal-header">
        <h5 style={classes.title} className="modal-title">
          {step === 'login'
            ? t('Connexion WordPress')
            : t('Déployer sur WordPress')}
        </h5>
        <button
          type="button"
          onClick={handleClose}
          style={classes.btnClose}
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          disabled={loading}
        />
      </div>

      <div style={classes.modalPad} className="modal-body">
        {step === 'login' ? (
          <form onSubmit={handleLogin} className="wp-login-form">
            <p className="wp-login-subtitle">
              {t('Connectez-vous à votre compte WordPress pour déployer le contenu.')}
            </p>
            <div className="wp-login-field">
              <label htmlFor="wp-username" style={labelStyle}>
                {t('Nom d’utilisateur')}
              </label>
              <input
                id="wp-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={fieldStyle}
                placeholder={t('wpDeploy.usernamePlaceholder', 'ex. Typix-neo')}
                autoComplete="username"
                disabled={loading}
              />
            </div>
            <div className="wp-login-field">
              <label htmlFor="wp-password" style={labelStyle}>
                {t('Mot de passe d’application')}
              </label>
              <input
                id="wp-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={fieldStyle}
                placeholder={t(
                  'wpDeploy.passwordPlaceholder',
                  'XXXX XXXX XXXX XXXX XXXX XXXX'
                )}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>

            <div className={`wp-help-box${helpOpen ? ' is-open' : ''}`}>
              <button
                type="button"
                className="wp-help-toggle"
                onClick={() => setHelpOpen((open) => !open)}
                aria-expanded={helpOpen}
              >
                <span className="wp-help-toggle-left">
                  <span className="wp-help-icon" aria-hidden="true">
                    ?
                  </span>
                  <span className="wp-help-title">
                    {t(
                      'wpDeploy.help.title',
                      'Où trouver mon nom d’utilisateur et mon mot de passe ?'
                    )}
                  </span>
                </span>
                <span className="wp-help-caret" aria-hidden="true" />
              </button>

              {helpOpen && (
                <div className="wp-help-body">
                  <ol className="wp-help-steps">
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">1</span>
                      <span className="wp-help-step-text">
                        <Trans
                          i18nKey="wpDeploy.help.step1"
                          components={{ strong: <strong /> }}
                        >
                         {t('Connectez-vous à WordPress et allez dans')} {' '}
                          <strong>{t('Utilisateurs (Users) ; Profil')}</strong>.
                        </Trans>
                      </span>
                    </li>
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">2</span>
                      <span className="wp-help-step-text">
                        <Trans
                          i18nKey="wpDeploy.help.step2"
                          components={{ strong: <strong /> }}
                        >
                          {t('Descendez jusqu\'à la section')} {' '}
                          <strong>{t('Mots de passe d\'application (Application Passwords)')}</strong>.
                        </Trans>
                      </span>
                    </li>
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">3</span>
                      <span className="wp-help-step-text">
                        <Trans
                          i18nKey="wpDeploy.help.step3"
                          components={{ strong: <strong /> }}
                        >
                          {t('Dans le champ')} {' '}
                          <strong>{t('New Application Password Name')}</strong>,
                          {t('saisissez un nom reconnaissable (ex. Typix-neo) :')} {' '}
                          {t('c\'est votre nom d\'utilisateur.')}
                        </Trans>
                      </span>
                    </li>
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">4</span>
                      <span className="wp-help-step-text">
                        <Trans
                          i18nKey="wpDeploy.help.step4"
                          components={{ strong: <strong /> }}
                        >
                          {t('Cliquez sur')} {' '}
                          <strong>{t('Add Application Password')}</strong>.
                        </Trans>
                      </span>
                    </li>
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">5</span>
                      <span className="wp-help-step-text">
                        <Trans i18nKey="wpDeploy.help.step5">
                          {t('WordPress génère un mot de passe unique, affiché une')} {' '}
                          {t('seule fois.')}
                        </Trans>
                      </span>
                    </li>
                    <li className="wp-help-step">
                      <span className="wp-help-step-number">6</span>
                      <span className="wp-help-step-text">
                        <Trans i18nKey="wpDeploy.help.step6">
                          {t('Reportez le nom et le mot de passe dans les champs')} {' '}
                          {t('ci-dessus.')}
                        </Trans>
                      </span>
                    </li>
                  </ol>

                  <div className="wp-help-warning" role="note">
                    <span className="wp-help-warning-icon" aria-hidden="true" />
                    <p className="wp-help-warning-text">
                      <Trans
                        i18nKey="wpDeploy.help.warning"
                        components={{ strong: <strong /> }}
                      >
                        <strong>{t('Important :')}</strong> {t('copiez le mot de passe')} {' '}
                        {t('immédiatement (bouton Copy) et conservez-le en lieu sûr.')}
                        {t('Une fois la fenêtre fermée, il ne pourra plus être')} {' '}
                        {t('récupéré — il faudra en générer un nouveau.')}
                      </Trans>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="wp-login-error">
                {error}
              </div>
            )}
            <div style={{ ...classes.boxBtConfirm, gap: 12 }}>
              <button
                type="button"
                style={buttonSecondaryStyle}
                onClick={() => {
                  setError('');
                  setStep('form');
                }}
                disabled={loading}
              >
                {t('Retour')}
              </button>
              <button
                type="submit"
                style={{
                  ...buttonPrimaryStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                disabled={loading}
              >
                {loading ? t('Connexion...') : t('Se connecter')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleDeploy}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="wp-keyword" style={labelStyle}>
                {t('Mot-clé')}
              </label>
              <input
                id="wp-keyword"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={fieldStyle}
                disabled={loading}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="wp-slug"
                style={{ ...labelStyle, display: 'flex', alignItems: 'center', marginBottom: '6px' }}
              >
                {t('Slug')} <span style={{ color: '#c33' }}>*</span>
                <span className="wp-slug-tooltip-wrapper">
                  <img
                    src="/Images/icon-tooltip.png"
                    alt="info"
                    className="wp-slug-info-icon"
                  />
                  <span className="wp-slug-tooltip-text" role="tooltip">
                    {t(
                      'wpDeploy.slugTooltip',
                      'Le slug est la partie de l’URL qui identifie votre page (ex : votre-site.com/slug). Il doit être court, en minuscules, sans accents ni caractères spéciaux, avec des tirets entre les mots.'
                    )}
                  </span>
                </span>
              </label>
              <input
                id="wp-slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                style={fieldStyle}
                placeholder={t('ex: mon-article-seo')}
                required
                disabled={loading}
              />
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, marginBottom: 0 }}>
                {t('Généré automatiquement à partir du mot-clé, modifiable si besoin.')}
              </p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="wp-content" style={labelStyle}>
                {t('Contenu')}
              </label>
              <textarea
                id="wp-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                style={{ ...fieldStyle, minHeight: 220, resize: 'vertical' }}
                disabled={loading}
              />
            </div>
            {error && (
              <div style={{ color: '#c33', fontSize: 14, marginBottom: 12 }}>
                {error}
              </div>
            )}
            <div style={{ ...classes.boxBtConfirm, gap: 12 }}>
              <button
                type="button"
                style={buttonSecondaryStyle}
                onClick={handleClose}
                disabled={loading}
              >
                {t('Annuler')}
              </button>
              <button
                type="submit"
                style={{
                  ...buttonPrimaryStyle,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                disabled={loading}
              >
                {loading ? t('Déploiement...') : t('Déployer')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default WordPressDeployModal;